import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Link,
  Outlet,
  useLocation,
  useOutlet,
  useParams,
} from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  useMotionValue,
  useTransform,
  animate,
} from 'framer-motion';
import { fetchWithAuth, handleApiError } from 'helpers/fetchWithAuth';
import { formatNumber } from 'components/DigitalCI/DigitalCI.jsx';

export const CecbDataContext = createContext(null);
export const useCecbData = () => useContext(CecbDataContext);
import './styles.css';

/* ============================================================
   Thème clair / sombre
   ============================================================ */
/* Le basculement est piloté par le toggle global de l'app
   (components/Settings/DarkModeToggle), qui pose `data-theme` sur <body>.
   Ici on se contente d'observer ce thème pour recolorer les graphiques
   Chart.js, qui ne réagissent pas seuls aux changements de variables CSS. */
function readBodyTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.body.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function useTheme() {
  const [theme, setTheme] = useState(readBodyTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readBodyTheme()));
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    // Synchronise si le thème a changé avant le montage.
    setTheme(readBodyTheme());
    return () => observer.disconnect();
  }, []);

  return { theme };
}

/* ============================================================
   Motion primitives (framer-motion) — polish partagé
   ============================================================ */

/* Révélation au scroll : remplace .anim-up + .d1..d5.
   Respecte prefers-reduced-motion (apparition instantanée). */
export function Reveal({
  children,
  delay = 0,
  y = 18,
  className,
  as = 'div',
  style,
  lift = false,
  ...rest
}) {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] ?? motion.div;

  if (reduce) {
    const Tag = as;
    // `layout`/`layoutId` sont des props framer-motion : ne pas les transmettre
    // à un tag DOM brut (avertissement React) dans le repli sans animation.
    const { layoutId, layout, ...domRest } = rest;
    void layoutId;
    void layout;
    return (
      <Tag className={className} style={style} {...domRest}>
        {children}
      </Tag>
    );
  }

  // `lift` gère le survol côté framer-motion : sans lui, le transform inline
  // laissé après l'animation d'entrée écraserait un :hover CSS.
  const hover = lift
    ? {
        whileHover: {
          y: -6,
          transition: { type: 'spring', stiffness: 320, damping: 22 },
        },
      }
    : {};

  return (
    <MotionTag
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, ease: 'easeOut', delay }}
      {...hover}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

Reveal.propTypes = {
  children: PropTypes.node,
  delay: PropTypes.number,
  y: PropTypes.number,
  className: PropTypes.string,
  as: PropTypes.string,
  style: PropTypes.object,
  lift: PropTypes.bool,
};

/* Compteur animé from → value (0 → value par défaut), formaté via
   formatNumber. Se déclenche une fois à l'entrée dans le viewport. */
export function CountUp({
  value,
  from = 0,
  round,
  duration = 1.1,
  delay = 0,
  format = formatNumber,
  className,
  prefix = '',
  suffix = '',
}) {
  const reduce = useReducedMotion();
  const mv = useMotionValue(from);
  const rounded = useTransform(
    mv,
    (v) => `${prefix}${format(v, round)}${suffix}`,
  );
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (reduce || !started) return;
    const controls = animate(mv, value ?? 0, {
      duration,
      delay,
      ease: [0.16, 1, 0.3, 1],
    });
    return controls.stop;
  }, [value, duration, delay, reduce, started, mv]);

  if (reduce) {
    return (
      <span className={className}>
        {prefix}
        {format(value, round)}
        {suffix}
      </span>
    );
  }

  return (
    <motion.span
      className={className}
      onViewportEnter={() => setStarted(true)}
      viewport={{ once: true }}
    >
      {rounded}
    </motion.span>
  );
}

CountUp.propTypes = {
  value: PropTypes.number,
  from: PropTypes.number,
  round: PropTypes.number,
  duration: PropTypes.number,
  delay: PropTypes.number,
  format: PropTypes.func,
  className: PropTypes.string,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
};

/* Barre qui se remplit de 0 → width à l'entrée dans le viewport.
   Conserve les conventions de couleur/opacité de .progress-fill. */
export function AnimatedBar({
  width,
  color,
  className = 'progress-fill',
  delay = 0,
  style,
}) {
  const reduce = useReducedMotion();
  const target = `${width}%`;

  if (reduce) {
    return (
      <div
        className={className}
        style={{ width: target, background: color, ...style }}
      />
    );
  }

  return (
    <motion.div
      className={className}
      style={{ background: color, ...style }}
      initial={{ width: 0 }}
      whileInView={{ width: target }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
    />
  );
}

AnimatedBar.propTypes = {
  width: PropTypes.number,
  color: PropTypes.string,
  className: PropTypes.string,
  delay: PropTypes.number,
  style: PropTypes.object,
};
/* Petit wrapper Material Symbols */
export function Icon({ name, fill = false, className = '', style }) {
  return (
    <span className={`msym${fill ? ' fill' : ''} ${className}`} style={style}>
      {name}
    </span>
  );
}

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  fill: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

/* Badge d'étiquette énergétique CECB (A → G) */
export function CecbBadge({ grade, size }) {
  const g = String(grade).toLowerCase();
  return (
    <span
      className={`cecb-badge cecb-badge--${g}${size === 'sm' ? ' cecb-badge--sm' : ''}`}
    >
      {String(grade).toUpperCase()}
    </span>
  );
}

CecbBadge.propTypes = {
  grade: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm']),
};

/* ============================================================
   AnimatedOutlet — transitions entre pages du module
   Fondu + léger glissement à chaque navigation. L'outlet est
   « gelé » à son premier rendu pour que la page sortante reste
   affichée pendant son animation de sortie (AnimatePresence).
   ============================================================ */
function FrozenOutlet() {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
}

export function AnimatedOutlet() {
  const { pathname } = useLocation();
  const reduce = useReducedMotion();

  if (reduce) return <Outlet />;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={pathname}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
      >
        <FrozenOutlet />
      </motion.div>
    </AnimatePresence>
  );
}

/* Carte de métrique (KPI) */
export function MetricCard({ label, value, unit, accent, flex }) {
  return (
    <div className={`card metric-card ${accent ? ' metric-card--accent' : ''}`}>
      <div className="eyebrow">{label}</div>
      <div
        className={`metric-card__value num ${flex ? ' metric-card-flex' : ''}`}
      >
        {value} {unit && <span className="metric-card__unit">{unit}</span>}
      </div>
    </div>
  );
}

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unit: PropTypes.string,
  accent: PropTypes.bool,
  flex: PropTypes.bool,
};

/* Barre de progression libellée (déperditions, impacts…) */
export function ProgressRow({
  label,
  pct,
  maxPct,
  color,
  element,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) {
  const scaledWidth = maxPct > 0 ? (pct / maxPct) * 90 : 0;
  return (
    <div
      className="progress-row"
      data-element={element}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="progress-row__head">
        <span>{label}</span>
        <span className="num">{pct}%</span>
      </div>
      <div className="progress-track">
        <div
          className="progress-fill"
          style={{ width: `${scaledWidth}%`, background: color }}
        />
      </div>
    </div>
  );
}

ProgressRow.propTypes = {
  label: PropTypes.string.isRequired,
  pct: PropTypes.number.isRequired,
  maxPct: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  element: PropTypes.string.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onClick: PropTypes.func,
};

/* Items de navigation — exporté pour rester la source de vérité unique */
export const NAV_ITEMS = [
  {
    key: 'dashboard',
    label: 'Tableau de bord',
    icon: 'grid_view',
    path: '/digitalcecb',
  },
  {
    key: 'etat',
    label: 'État initial',
    icon: 'analytics',
    path: '/digitalcecb/etat-initial',
    children: [
      {
        key: 'composants',
        label: 'Composants',
        path: '/digitalcecb/etat-initial/composants',
      },
    ],
  },
  {
    key: 'variantes',
    label: 'Variantes',
    icon: 'compare_arrows',
    path: '/digitalcecb/variantes',
  },
  {
    key: 'couts',
    label: 'Coûts et subventions',
    icon: 'payments',
    path: '/digitalcecb/subventions',
  },
  {
    key: 'marche-a-suivre',
    label: 'Marche à suivre',
    icon: 'checklist',
    path: '/digitalcecb/marche-a-suivre',
  },
];

/* ============================================================
   AppShell — composant commun (sidebar seule, plein écran)
   props:
     - project: { name, id }
     - children: contenu de la page
   ============================================================ */
export default function AppShell({ cecbData, children }) {
  const { pathname } = useLocation();
  const { cecbPlusId } = useParams();

  const base = cecbPlusId ? `/digitalcecb/${cecbPlusId}` : '/digitalcecb';

  const variantChildren = (cecbData?.variants ?? []).map((v) => ({
    key: v.id,
    label: v.title,
    path: `${base}/variantes/${v.id}`,
  }));

  const resolvedItems = NAV_ITEMS.map((item) => ({
    ...item,
    path: base + item.path.replace('/digitalcecb', ''),
    children:
      item.key === 'variantes'
        ? variantChildren
        : item.children?.map((sub) => ({
            ...sub,
            path: base + sub.path.replace('/digitalcecb', ''),
          })),
  }));

  const isActive = (item) =>
    item.path === base ? pathname === base : pathname.startsWith(item.path);

  return (
    <CecbDataContext.Provider value={cecbData}>
      <div className="digitalcecb">
        <aside className="sidebar">
          <div className="sidebar__brand">
            <div className="sidebar__logo">
              <Icon name="brick" />
            </div>
            <span className="sidebar__brand-name">I-NEEA · CECB Plus</span>
          </div>

          <div className="sidebar__meta">
            <h2>{cecbData?.building?.buildingName}</h2>
            <p>ID: {cecbData?.location?.egid}</p>
          </div>

          <nav className="sidebar__nav">
            {resolvedItems.map((item) => (
              <React.Fragment key={item.key}>
                <Link
                  to={item.path}
                  className={`nav-item${isActive(item) ? ' nav-item--active' : ''}`}
                >
                  <Icon name={item.icon} fill={isActive(item)} />
                  <span>{item.label}</span>
                </Link>

                {item.children &&
                  isActive(item) &&
                  item.children.map((sub) => (
                    <Link
                      key={sub.key}
                      to={sub.path}
                      className={`nav-subitem${pathname.startsWith(sub.path) ? ' nav-subitem--active' : ''}`}
                    >
                      {sub.label}
                    </Link>
                  ))}
              </React.Fragment>
            ))}
          </nav>
        </aside>

        <main className="app-main">{children}</main>
      </div>
    </CecbDataContext.Provider>
  );
}

AppShell.propTypes = {
  cecbData: PropTypes.object,
  children: PropTypes.node,
};

/* Texte éditable inline — clic pour éditer (admins), sauvegarde au blur
   via PATCH /cecbplus-digital/{id}/patch-improvements/ */
export function EditableText({
  value,
  cecbPlusId,
  geakPath,
  editable = false,
  as: Tag = 'p',
  inline = false,
  className = 'measure-card__desc',
  placeholder = 'Ajouter une description…',
  emptyLabel = 'Aucune description',
  onSaved,
}) {
  const [text, setText] = useState(value ?? '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!editing) setText(value ?? '');
  }, [value, editing]);

  const save = async () => {
    setEditing(false);
    if (!cecbPlusId || !geakPath) return;
    // Rien à sauvegarder si le texte n'a pas changé (évite un PATCH à chaque blur).
    if (text === (value ?? '')) return;
    setSaving(true);
    try {
      await fetchWithAuth(
        'Saving text',
        `/cecbplus-digital/${cecbPlusId}/patch-improvements/`,
        {
          method: 'PATCH',
          body: JSON.stringify({ path: geakPath, value: text }),
          headers: { 'Content-Type': 'application/json' },
        },
      );
      // Remonte la nouvelle valeur pour rafraîchir le contexte cecbData.
      onSaved?.(geakPath, text);
    } catch (error) {
      handleApiError(error, 'Échec de la sauvegarde');
      setText(value ?? '');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setText(value ?? '');
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      save();
    }
  };

  if (editing && editable) {
    return inline ? (
      <input
        type="text"
        className={`${className} ${className}--edit`}
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
      />
    ) : (
      <textarea
        className={`${className} ${className}--edit`}
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (!editable) {
    return (
      <Tag className={className}>
        {text || (
          <span className={`${className}--placeholder`}>{emptyLabel}</span>
        )}
      </Tag>
    );
  }

  return (
    <Tag
      className={`${className} ${className}--editable${saving ? ` ${className}--saving` : ''}`}
      onClick={() => cecbPlusId && setEditing(true)}
      title={cecbPlusId ? 'Cliquer pour modifier' : undefined}
    >
      {text || (
        <span className={`${className}--placeholder`}>{placeholder}</span>
      )}
    </Tag>
  );
}

EditableText.propTypes = {
  value: PropTypes.string,
  cecbPlusId: PropTypes.string,
  geakPath: PropTypes.string.isRequired,
  editable: PropTypes.bool,
  as: PropTypes.elementType,
  inline: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  emptyLabel: PropTypes.string,
  onSaved: PropTypes.func,
};

/* Carte mesure/élément — priorité, icône, titre, texte éditable et
   footer optionnel. Partagée entre le catalogue de mesures et l'état
   actuel des éléments. */
export function MeasureCard({
  icon,
  iconTech = false,
  title,
  priority,
  text,
  cecbPlusId,
  geakPath,
  editable = false,
  footer,
  stateClass = '',
  delay = 0,
  layout = false,
  layoutId,
  dropzone,
}) {
  const cecbData = useCecbData();
  const level = cecbData?.priorities?.levels?.find((l) => l.Index === priority);

  // Handlers de drag & drop posés sur la carte entière (cible plus large que
  // la bande de miniatures), fournis par l'appelant.
  const { className: dropClass = '', ...dropHandlers } = dropzone ?? {};

  return (
    <Reveal
      as="article"
      delay={delay}
      className={`measure-card${stateClass}${dropClass}`}
      layout={layout || undefined}
      layoutId={layoutId}
      {...dropHandlers}
    >
      <span
        className="measure-card__priority"
        style={{ background: level?.Color, color: level?.Color }}
        title={level?.Text ?? `Priorité ${priority}`}
      />
      <div
        className={`measure-card__icon${iconTech ? ' measure-card__icon--tech' : ''}`}
      >
        <Icon name={icon} fill />
      </div>
      <h4 className="measure-card__title">{title}</h4>
      {geakPath && (
        <EditableText
          value={text ?? ''}
          cecbPlusId={cecbPlusId}
          geakPath={geakPath}
          editable={editable}
        />
      )}
      {footer && <div className="measure-card__footer">{footer}</div>}
    </Reveal>
  );
}

MeasureCard.propTypes = {
  icon: PropTypes.string.isRequired,
  iconTech: PropTypes.bool,
  title: PropTypes.string.isRequired,
  priority: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  text: PropTypes.string,
  cecbPlusId: PropTypes.string,
  geakPath: PropTypes.string,
  editable: PropTypes.bool,
  footer: PropTypes.node,
  stateClass: PropTypes.string,
  delay: PropTypes.number,
  layout: PropTypes.bool,
  layoutId: PropTypes.string,
  dropzone: PropTypes.object,
};

/* Légende des niveaux de priorité (couleur + texte), lue depuis
   cecbData.priorities.levels. Rendu vide si la donnée est absente. */
export function PriorityLegend({ className = '' }) {
  const cecbData = useCecbData();
  const levels = cecbData?.priorities?.levels;
  if (!levels?.length) return null;

  return (
    <ul className={`priority-legend ${className}`}>
      {levels.map((level) => (
        <li key={level.Index} className="priority-legend__item">
          <span
            className="priority-legend__dot"
            style={{ background: level.Color }}
          />
          {level.Text}
        </li>
      ))}
    </ul>
  );
}

PriorityLegend.propTypes = {
  className: PropTypes.string,
};
